import { useCallback, useEffect, useState } from 'react';
import { limitToCents } from '../../lib/amount';
import axios from 'axios';
import PageHeader from '../../components/layout/PageHeader';
import { tokens } from '../../components/forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatMoney } from '../../lib/datetime';
import { fullName } from '../../lib/fullName';
import authService from '../../services/auth.service';
import agentService from '../../services/agent.service';
import managerService, { type ClientSummary } from '../../services/manager.service';
import commitmentsService from '../../services/commitments.service';
import portfolioService, { type ProductSummary } from '../../services/portfolio.service';
import type { ActiveCommitmentPolicy, Cadence, Commitment } from '../../types/commitments';
import { draftingAge } from '../../lib/commitments.helpers';
import { t } from '../../i18n/language';

interface DraftForm {
  user_id: string;
  product_id: string;
  cadence: Cadence;
  amount: string;
  start_date: string;
  end_date: string;
  day_of_month: string;
  day_of_week: string;
  notes: string;
}

function todayIsoDate(): string {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function defaultForm(): DraftForm {
  return {
    user_id: '',
    product_id: '',
    cadence: 'monthly',
    amount: '',
    start_date: todayIsoDate(),
    end_date: '',
    day_of_month: '1',
    day_of_week: '',
    notes: '',
  };
}

/**
 * Agent's commitment console:
 *  · top: drafting form (create after a verbal agreement on call)
 *  · bottom: list of commitments the agent has touched, status-grouped.
 *
 * Once drafted, the contract case lives in `contract_review_v1` —
 * the agent uses the existing /agent/cases/{id} screen to monitor signing
 * and escalate to manager via the workflow:transition events.
 */
export default function AgentCommitments(): JSX.Element {
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [activePolicy, setActivePolicy] = useState<ActiveCommitmentPolicy | null>(null);
  const [recent, setRecent] = useState<Commitment[]>([]);

  const [form, setForm] = useState<DraftForm>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [cancelBusyId, setCancelBusyId] = useState<string | null>(null);

  const loadInitial = useCallback(async () => {
    try {
      // Two distinct backend endpoints: agents get their own roster
      // (assigned_agent_id = caller), managers/super_admin get every
      // active client. The URL scope now matches the data scope.
      const role = authService.getRole();
      const clientsP = role === 'agent'
        ? agentService.listMyClients()
        : managerService.listClients();
      const [clientsRes, productsRes] = await Promise.all([
        clientsP,
        portfolioService.listProducts(),
      ]);
      setClients(clientsRes);
      setProducts(productsRes);
      if (clientsRes.length && !form.user_id) {
        setForm((f) => ({ ...f, user_id: clientsRes[0].id }));
      }
      if (productsRes.length && !form.product_id) {
        setForm((f) => ({ ...f, product_id: productsRes[0].id }));
      }
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Failed to load form data'));
    }
  // form deps intentionally omitted — only seed on first mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadActivePolicy = useCallback(async (productId: string) => {
    if (!productId) {
      setActivePolicy(null);
      return;
    }
    try {
      const policy = await commitmentsService.getActiveProductPolicy(productId);
      setActivePolicy(policy);
    } catch (e: unknown) {
      // 404 = product has no published policy yet; agent draft is disabled
      // implicitly because there is nothing to validate against. Anything else
      // (network, 5xx) used to land here too and was hidden, so the agent could
      // not tell "this product has no policy" from "we could not read it".
      setActivePolicy(null);
      const noPolicy = axios.isAxiosError(e) && e.response?.status === 404;
      if (!noPolicy) {
        setErr(getApiErrorMessage(e, 'Could not load the commitment policy for this product.'));
      }
    }
  }, []);

  const loadRecent = useCallback(async (userId: string) => {
    if (!userId) return;
    try {
      const items = await commitmentsService.listForClient(userId);
      setRecent(items);
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Failed to load commitments'));
    }
  }, []);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    loadActivePolicy(form.product_id);
  }, [form.product_id, loadActivePolicy]);

  useEffect(() => {
    loadRecent(form.user_id);
  }, [form.user_id, loadRecent]);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setErr(null);
    setFlash(null);
    if (form.end_date && form.start_date && form.end_date < form.start_date) {
      setErr('End date cannot be earlier than the start date.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await commitmentsService.draft({
        user_id: form.user_id,
        product_id: form.product_id,
        cadence: form.cadence,
        amount: form.amount,
        start_date: form.start_date,
        end_date: form.end_date || null,
        day_of_month: form.cadence === 'monthly' && form.day_of_month
          ? Number.parseInt(form.day_of_month, 10)
          : null,
        day_of_week: form.cadence === 'weekly' && form.day_of_week !== ''
          ? Number.parseInt(form.day_of_week, 10)
          : null,
        notes: form.notes || null,
      });
      setFlash(`Drafted commitment ${result.id}. Client must now sign the contract.`);
      loadRecent(form.user_id);
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Draft failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24, color: tokens.textPrimary }}>
      <PageHeader
        title="Deposit commitments"
        subtitle="Draft after the call, monitor signing, escalate to manager"
      />

      {err ? <div style={errBoxStyle}>{err}</div> : null}
      {flash ? <div style={successBoxStyle}>{flash}</div> : null}

      <section style={cardStyle}>
        <h2 style={h2Style}>Draft new commitment</h2>
        <form onSubmit={onSubmit} style={{ display: 'grid', gap: 12 }}>
          <div style={twoColStyle}>
            <label style={labelStyle}>
              Client
              {/* */}
              <select
                value={form.user_id}
                onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                style={inputStyle}
              >
                {[...clients]
                  .sort((a, b) => fullName(a).localeCompare(fullName(b)))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {fullName(c)} — {c.email}
                    </option>
                  ))}
              </select>
            </label>
            <label style={labelStyle}>
              Product
              {/* */}
              <select
                value={form.product_id}
                onChange={(e) => setForm({ ...form, product_id: e.target.value })}
                style={inputStyle}
              >
                {[...products]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </select>
            </label>
          </div>

          {activePolicy ? (
            <div style={infoBoxStyle}>
              <strong>Policy v{activePolicy.version}</strong> · cadences:{' '}
              {activePolicy.allowed_cadences.join(', ')} · amount:{' '}
              {activePolicy.min_amount} – {activePolicy.max_amount ?? '∞'} · grace:{' '}
              {activePolicy.grace_days}d · reminder lead:{' '}
              {activePolicy.reminder_lead_days}d
            </div>
          ) : null}

          <div style={twoColStyle}>
            <label style={labelStyle}>
              Cadence
              {/* */}
              <select
                value={form.cadence}
                onChange={(e) => setForm({ ...form, cadence: e.target.value as Cadence })}
                style={inputStyle}
              >
                <option value="monthly">Monthly</option>
                <option value="one_time">One-time</option>
                <option value="weekly">Weekly</option>
              </select>
            </label>
            <label style={labelStyle}>
              Amount
              {/* */}
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                min="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: limitToCents(e.target.value) })}
                style={inputStyle}
              />
            </label>
          </div>

          <div style={twoColStyle}>
            <label style={labelStyle}>
              Start date (agreed on call)
              {/* */}
              <input
                type="date"
                value={form.start_date}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              End date (blank = open-ended)
              {/* */}
              <input
                type="date"
                value={form.end_date}
                min={form.start_date || new Date().toISOString().split('T')[0]}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                style={inputStyle}
              />
            </label>
          </div>

          {form.cadence === 'monthly' ? (
            <label style={labelStyle}>
              Day of month (1–28)
              {/* */}
              <input
                type="number"
                min="1"
                max="28"
                value={form.day_of_month}
                onChange={(e) => setForm({ ...form, day_of_month: e.target.value })}
                style={inputStyle}
              />
            </label>
          ) : null}
          {form.cadence === 'weekly' ? (
            <label style={labelStyle}>
              Day of week (0=Mon, 6=Sun)
              {/* */}
              <input
                type="number"
                min="0"
                max="6"
                value={form.day_of_week}
                onChange={(e) => setForm({ ...form, day_of_week: e.target.value })}
                style={inputStyle}
              />
            </label>
          ) : null}

          <label style={labelStyle}>
            Internal notes
            {/* */}
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={1000}
              rows={3}
              style={inputStyle}
            />
          </label>

          <button
            type="submit"
            disabled={submitting || !form.user_id || !form.product_id}
            style={btnPrimary}
          >
            {submitting ? 'Drafting…' : 'Draft commitment + generate contract PDF'}
          </button>
        </form>
      </section>

      <section style={{ ...cardStyle, marginTop: 24 }}>
        <h2 style={h2Style}>This client&apos;s commitments</h2>
        {recent.length === 0 ? (
          <p style={{ color: tokens.textMuted }}>None yet.</p>
        ) : (
          <div className="overflow-x-auto">
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Product</th>
                <th style={thStyle}>Cadence</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Contract case</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((c) => (
                <tr key={c.id}>
                  <td style={tdStyle}>{c.product_name}</td>
                  <td style={tdStyle}>{c.cadence}</td>
                  <td style={tdStyle}>
                    {formatMoney(c.amount, c.currency_code)}
                  </td>
                  <td style={tdStyle}>
                    <span style={statusPill(c.status)}>{c.status}</span>
                    {c.status === 'drafting' && (() => {
                      // Surface how long an unsigned draft has been waiting so
                      // it doesn't sit invisibly forever (no auto-expiry — the
                      // agent still owns the follow-up).
                      const age = draftingAge(c.created_at);
                      return (
                        <div
                          style={{
                            fontSize: 11,
                            marginTop: 4,
                            color: age.stale ? tokens.errorText : tokens.textMuted,
                          }}
                        >
                          {age.label}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={tdStyle}>
                    <a href={`/agent/cases/${c.contract_case_id}`} style={{ color: tokens.gold }}>
                      {c.contract_state_slug}
                    </a>
                  </td>
                  <td style={tdStyle}>{renderCancelAction(c, cancelBusyId, requestCancel)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </div>
  );

  async function requestCancel(commitmentId: string): Promise<void> {
    const reason = prompt(
      t('Reason for cancellation request (min 5 chars, max 1000 chars). Manager will approve and assign follow-up.'),
    );
    if (!reason || reason.trim().length < 5) return;
    if (reason.trim().length > 1000) {
      alert(t('Reason must not exceed 1000 characters.'));
      return;
    }
    setCancelBusyId(commitmentId);
    setErr(null);
    setFlash(null);
    try {
      await commitmentsService.requestCancellation(commitmentId, reason.trim());
      setFlash('Cancellation requested — manager will review and assign follow-up.');
      loadRecent(form.user_id);
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Request failed'));
    } finally {
      setCancelBusyId(null);
    }
  }
}

const cardStyle: React.CSSProperties = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 12,
  padding: 20,
};
const h2Style: React.CSSProperties = { margin: '0 0 12px 0', fontSize: 16 };
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: tokens.textLabel,
  // Lets the grid column shrink below its content — a long client email would
  // otherwise force the auto-fit column wide and overflow the form on mobile.
  minWidth: 0,
  overflowWrap: 'anywhere',
};
const inputStyle: React.CSSProperties = {
  background: tokens.surfaceMuted,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: tokens.textPrimary,
};
const twoColStyle: React.CSSProperties = {
  display: 'grid',
  // Two columns when there's room; collapses to one on narrow screens.
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
  gap: 12,
};
const btnPrimary: React.CSSProperties = {
  background: tokens.gold,
  color: tokens.navy,
  border: 'none',
  borderRadius: 8,
  padding: '10px 16px',
  fontWeight: 600,
  cursor: 'pointer',
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  color: tokens.textPrimary,
  fontSize: 13,
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: `1px solid ${tokens.border}`,
  color: tokens.textMuted,
};
const tdStyle: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: `1px solid ${tokens.border}`,
};
const errBoxStyle: React.CSSProperties = {
  background: tokens.errorBg,
  border: `1px solid ${tokens.errorBorder}`,
  color: tokens.errorText,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
};
const successBoxStyle: React.CSSProperties = {
  background: tokens.successBg,
  border: `1px solid ${tokens.successBorder}`,
  color: tokens.successText,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
};
const infoBoxStyle: React.CSSProperties = {
  background: tokens.infoBg,
  border: `1px solid ${tokens.infoBorder}`,
  color: tokens.textPrimary,
  padding: 10,
  borderRadius: 8,
  fontSize: 12,
};

const btnDanger: React.CSSProperties = {
  background: tokens.errorBorder,
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '4px 10px',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 600,
};

function renderCancelAction(
  c: Commitment,
  busyId: string | null,
  onRequest: (id: string) => void,
): JSX.Element | null {
  if (c.status === 'active' || c.status === 'paused') {
    return (
      <button
        type="button"
        onClick={() => onRequest(c.id)}
        style={btnDanger}
        disabled={busyId === c.id}
      >
        Request cancellation
      </button>
    );
  }
  if (c.status === 'pending_cancellation') {
    return (
      <span style={{ color: tokens.textMuted, fontSize: 11 }}>awaiting manager</span>
    );
  }
  return null;
}

function statusPill(status: string): React.CSSProperties {
  const palette: Record<string, [string, string]> = {
    drafting: [tokens.infoBg, tokens.infoBorder],
    pending_manager: [tokens.successBg, tokens.successBorder],
    active: [tokens.successBg, tokens.successText],
    paused: [tokens.errorBg, tokens.errorBorder],
    cancelled: [tokens.errorBg, tokens.errorText],
    completed: [tokens.surfaceMuted, tokens.border],
  };
  const [bg, border] = palette[status] ?? [tokens.surfaceMuted, tokens.border];
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    background: bg,
    border: `1px solid ${border}`,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  };
}
